import type ActivityMetrics from "./ActivityMetrics";

export interface TeamLineThresholds {
  noReportMinCount: number;
  noReportMinShare: number;
  didNotTakePlaceMinCount: number;
  didNotTakePlaceMinShare: number;
}

export type TeamLine =
  | { kind: "noActivity" }
  | { kind: "noReport"; count: number; due: number }
  | { kind: "didNotTakePlace"; count: number };

/**
 * The one fact worth stating about an employee's period, or null, which is the normal case. Rules
 * run in a fixed order and the first match wins, so two facts are never combined. A count rule
 * needs both its count and its share: the count keeps a small denominator from inflating the
 * share, the share keeps a large one from making any count matter.
 */
export function teamLineFor(metrics: ActivityMetrics | null, thresholds: TeamLineThresholds): TeamLine | null {
  if (!metrics) return null;

  const states = metrics.appointmentStates;
  const appointments = states?.total ?? metrics.meetingsCount;
  // An unknown marked-done count could hide the only activity, so it never proves there was none.
  const hasNoActivity =
    appointments === 0 && metrics.recapsCount === 0 && metrics.dismissalsCount === 0 && metrics.markedDoneCount === 0;
  if (hasNoActivity) return { kind: "noActivity" };

  if (!states) return null;

  const due = states.total - states.planned;
  if (states.noReport >= thresholds.noReportMinCount && states.noReport >= thresholds.noReportMinShare * due) {
    return { kind: "noReport", count: states.noReport, due };
  }

  if (
    states.didNotTakePlace >= thresholds.didNotTakePlaceMinCount &&
    states.didNotTakePlace >= thresholds.didNotTakePlaceMinShare * states.total
  ) {
    return { kind: "didNotTakePlace", count: states.didNotTakePlace };
  }

  return null;
}
